import * as preact from "preact";
import { ComponentChild } from "preact";
import { BaseComponent } from "../Common/BaseComponent/BaseComponent";

export enum DateFormat
{
    MonthAndDay = 1,
    MonthAndYear = 2,
    MonthDayTime = 3,
    SimpleDateTime = 4,
    SimpleDate = 5,
    LongDate = 6,
    ShortDate = 7
}


export class Common
{
    public static serialize = JSON.stringify;
    public static deserialize = JSON.parse;
    public static trace = console.debug;

    public static formatDate(date: Date, format: DateFormat)
    {
        if (!date)
        {
            return null;
        }

        var options: Intl.DateTimeFormatOptions = {};
        switch(format)
        {
            case DateFormat.MonthAndDay:
                options.month = "long";
                options.day = "numeric";
                break;
            case DateFormat.MonthAndYear:
                options.month = "long";
                options.year = "numeric";
                break;
            case DateFormat.MonthDayTime:
                options.month = "long";
                options.day = "numeric";
                options.hour = "numeric";
                options.minute = "numeric";
                break;
            case DateFormat.SimpleDateTime:
                options.hour = "numeric";
                options.minute = "numeric";
            case DateFormat.SimpleDate:
                options.month = "numeric";
                options.day = "numeric";
                options.year = "numeric";
                break;
            case DateFormat.LongDate:
                options.dateStyle = "full";
                break;
            case DateFormat.ShortDate:
                options.dateStyle = "medium";
                break;

        }
        let formatter = new Intl.DateTimeFormat(navigator.language, options);
        return formatter.format(date);

    }

    public static addCssClass(element: HTMLElement, className: string)
    {
        if (!element.classList.contains(className))
        {
            element.classList.add(className);
        }
    }

    public static removeCssClass(element: HTMLElement, className: string)
    {
        element.classList.remove(className);
    }

    public static containsCssClass(element: HTMLElement, className: string)
    {
        return element.classList.contains(className);
    }

    public static getBounds(element: HTMLElement): { x, y, width, height }
    {
        var rect = element.getBoundingClientRect();
        return {
            x: rect.left + window.scrollX,
            y: rect.top + window.scrollY,
            width: rect.width,
            height: rect.height
        };
    }

    public static hide(element: HTMLElement | BaseComponent)
    {
        if (BaseComponent.isComponent(element))
        {
            element.hide();
        }
        else
        {
            if (element.style.display !== "none")
            {
                (element as any).oldDisplay = element.style.display;
                element.style.display = "none";
            }
        }
    }

    public static show(element: HTMLElement | BaseComponent)
    {
        if (BaseComponent.isComponent(element))
        {
            element.show();
        }
        else
        {
            if (element.style.display === "none")
            {
                element.style.display = (element as any).oldDisplay || "";
                delete (element as any).oldDisplay;
            }
        }
    }

    public static display(element: HTMLElement | BaseComponent, show: boolean)
    {
        if (show)
        {
            this.show(element);
        }
        else
        {
            this.hide(element);
        }
        return show;
    }

    public static renderElement(jsxElement: ComponentChild): DocumentFragment
    {
        let fragment = document.createDocumentFragment();
        preact.render(jsxElement, fragment);
        return fragment;
    }

    public static appendChild(element: HTMLElement, jsxElement: ComponentChild)
    {
        element.appendChild(this.renderElement(jsxElement));
    }

    public static getSelectValue(select: HTMLSelectElement)
    {
        return select.options[select.selectedIndex]?.value || null;
    }

    public static setSelectValue(select: HTMLSelectElement, value: string)
    {
        select.value = value;
    }

    public static focus(element: HTMLElement)
    {
        try { element.focus() }
        catch (ex) { }
    }

    public static getParent(element: HTMLElement, type: string, className?: string)
    {
        type = type.toUpperCase();
        while (element && element.nodeName != type || (element && className && !this.containsCssClass(element, className)))
        {
            element = element.parentNode as HTMLElement;
        }

        return element;
    }

	public static getMediaWidth(): number
    {
        return Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
	}

    public static flexDateToDate(flexDate): Date
    {
        if (flexDate?.RawYear)
        {
            return new Date(flexDate.RawYear, (flexDate.RawMonth || 0) - 1, flexDate.RawDay || 0, flexDate.RawHours || 0, flexDate.RawMinutes || 0, flexDate.RawSeconds || 0);
        }

        return null;
    }

    public static lineDate(flexDate)
    {
        let date = this.flexDateToDate(flexDate);
        if (!date)
        {
            return "";
        }

        let value: string;

        switch (flexDate.Type)
        {
            case 1:  //Date Time
                value = this.formatDate(date, DateFormat.MonthDayTime);
                //value = utc.localeFormat("D");
                //value = date.localeFormat("MMMM d") + " " + date.localeFormat("t");
                break;
            case 2:  //Date
                value = this.formatDate(date, DateFormat.MonthAndDay);
                //value = date.localeFormat("MMMM d");
                break;
            case 3:  //Month and Year
                //value = date.localeFormat("MMMM");
                value = this.formatDate(date, DateFormat.MonthAndYear);
                break;
            case 4:  //Year
                if (flexDate.RawYear < 0)
                {
                    value = flexDate.RawYear * -1 + " BCE";
                }
                else if (flexDate.RawYear < 1000)
                {
                    value = flexDate.RawYear + " CE";
                }
                else
                {
                    value = flexDate.RawYear;
                }
                break;
        }
        if (flexDate.IsCirca)
        {
            value = "ca. " + value;
        }
        return value;
    }

    public static formatFlexDate(flexDate, long?: boolean, skipCirca?: boolean) {
        let date = this.flexDateToDate(flexDate);
        if (!date)
        {
            return "";
        }

        let value: string;

        switch (flexDate.Type)
        {
            case 1:  //Date Time
                value = this.formatDate(date, DateFormat.SimpleDateTime);
                //value = utc.localeFormat("D");
                //value = date.localeFormat("d") + " " + date.localeFormat("t");
                break;
            case 2:  //Date
                value = long ? this.formatDate(date, DateFormat.ShortDate) : this.formatDate(date, DateFormat.SimpleDate);
                break;
            case 3:  //Month and Year
                value = this.formatDate(date, DateFormat.MonthAndYear);
                //value = date.localeFormat("MMMM yyyy");
                break;
            case 4:  //Year
                if (flexDate.RawYear < 0)
                {
                    value = flexDate.RawYear * -1 + " BCE";
                }
                else if (flexDate.RawYear < 1000)
                {
                    value = flexDate.RawYear + " CE";
                }
                else
                {
                    value = flexDate.RawYear;
                }
                break;
        }
        if (!skipCirca && flexDate.IsCirca)
        {
            value = "ca. " + value;
        }
        return value;
    }

    public static getTimezoneInfo(date: Date, coordinates: string, callback: (jqXHR: JQueryXHR, status: string) => void)
    {
        let timestamp = date.getTime() / 1000;
        $.ajax(`https://maps.googleapis.com/maps/api/timezone/json?location=${coordinates}&timestamp=${timestamp}&key=AIzaSyAoHplYYWCxx79pR_z6BCTxwUCYevNdQb8`,
            {
                complete: callback
            });
    }

    public static getYoutubeInfo(videoId: string, callback: (jqXHR: JQueryXHR, status: string) => void)
    {
        $.ajax(`https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=AIzaSyAoHplYYWCxx79pR_z6BCTxwUCYevNdQb8&part=snippet,status`,
            {
                complete: callback
            });
    }

    public static toUTC(date)
    {
        if (!date)
        {
            return date;
        }
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
    }

    public static parseFlexDate(value: string, endRange?: boolean): any
    {
        //is this only a year
        value = value?.trim();
        if (!value)
        {
            return null;
        }

        let yearRegex = /^([0-9]+)\s*(bce?|b\.c\.(\.e)?|ad|a\.d\.|ce|c\.\.e)?$/gi
        let match = yearRegex.exec(value);
        if (match)
        {
            let year = parseInt(match[1], 10);
            if (match[2]?.indexOf("b") === 0)
            {
                year = year * -1;
            }
            if (endRange)
            {
                year++;
            }
            return {
                RawDay: 1,
                RawMonth: 1,
                RawYear: year
            };
        }

        let date = new Date(value);
        if (date)
        {
            return {
                RawDay: date.getDate(),
                RawMonth: date.getMonth() + 1,
                RawYear: date.getFullYear(),
                RawHours: date.getHours(),
                RawMinutes: date.getMinutes(),
                RawSeconds: date.getSeconds()
            };
        }

        return null;
    }

    public static serviceError()
    {
        return (err, param2, param3) =>
        {
            let exception = err._errorObject;

            if (exception.ExceptionType === "EventLibrary.Exceptions.InformationException")
            {
                alert(exception.Message);
            }
            else
            {
                alert("There was a problem loading or saving.  Please try again.");
            }
        }
    }

    public static indexOfElement(arry: any[], element: any, member?: string,)
    {
        if (!arry || arry.length === 0)
        {
            return null;
        }

        for (let i = 0; i < arry.length; i++)
        {
            if (member) {
                if (arry[i][member] === element)
                {
                    return i;
                }
            }
            else {
                if (arry[i] === element)
                {
                    return i;
                }
            }
        }

        return null;
    }

    public static youtubeId(url: string)
    {
        if (url?.match(/(youtube.com|youtu.be)/))
        {
            let match = url.match(/[&?]v=([^&]+)/);
            if (match)
            {
                return match[1];
            }

            //Find youtube ids in the following https://youtu.be/xyxyxyxxy
            match = url.match(/\/([a-zA-Z0-9_]{8,})/);
            if (match)
            {
                return match[1];
            }
        }
        return null;
    }
}

export var $$ = Common;